/**
 * NCR (Non-Conformance Report) List Page
 *
 * Shows all NCRs for the auditor.
 * Auditors can view, create, and manage non-conformance reports.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FileText, Search, Filter, Plus, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { formatDate } from '../../utils/formatting';

interface NCR {
  id: string;
  ncrNumber: string;
  companyName: string;
  applicationId: string;
  findingType: 'major' | 'minor' | 'observation';
  status: 'open' | 'in_progress' | 'pending_verification' | 'closed';
  issuedDate: string;
  dueDate: string;
  closedDate?: string;
  clauseReference: string;
  description: string;
}

export const NCRListPage: React.FC = () => {
  const { t } = useTranslation(['common', 'auditor']);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ncrs, setNcrs] = useState<NCR[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setNcrs([
        {
          id: 'ncr-001',
          ncrNumber: 'NCR-2025-001',
          companyName: 'ABC Food Manufacturing Sdn Bhd',
          applicationId: 'APP-2025-001',
          findingType: 'minor',
          status: 'closed',
          issuedDate: '2025-12-20',
          dueDate: '2026-01-20',
          closedDate: '2026-01-15',
          clauseReference: 'MHMS 2020 - 4.2.1',
          description: 'Halal policy document not signed by top management',
        },
        {
          id: 'ncr-002',
          ncrNumber: 'NCR-2025-002',
          companyName: 'Tasty Snacks Manufacturing',
          applicationId: 'APP-2025-004',
          findingType: 'major',
          status: 'in_progress',
          issuedDate: '2025-12-27',
          dueDate: '2026-01-27',
          clauseReference: 'MHMS 2020 - 5.3.2',
          description: 'Non-halal ingredients stored in same area without proper segregation',
        },
        {
          id: 'ncr-003',
          ncrNumber: 'NCR-2025-003',
          companyName: 'Premium Bakery Industries',
          applicationId: 'APP-2025-003',
          findingType: 'observation',
          status: 'open',
          issuedDate: '2025-12-28',
          dueDate: '2026-01-28',
          clauseReference: 'MHMS 2020 - 6.1.1',
          description: 'Employee training records not up to date',
        },
        {
          id: 'ncr-004',
          ncrNumber: 'NCR-2025-004',
          companyName: 'Global Ingredients Supply Co.',
          applicationId: 'APP-2025-002',
          findingType: 'minor',
          status: 'pending_verification',
          issuedDate: '2025-12-29',
          dueDate: '2026-01-29',
          clauseReference: 'MHMS 2020 - 4.3.1',
          description: 'Incomplete documentation for supplier halal certificates',
        },
      ]);
      setLoading(false);
    }, 500);
  }, []);

  const getStatusBadge = (status: NCR['status']) => {
    switch (status) {
      case 'closed':
        return <Badge variant="success" dot>{t('auditor:ncr.status.closed')}</Badge>;
      case 'in_progress':
        return <Badge variant="warning" dot>{t('auditor:ncr.status.inProgress')}</Badge>;
      case 'open':
        return <Badge variant="error" dot>{t('auditor:ncr.status.open')}</Badge>;
      case 'pending_verification':
        return <Badge variant="info" dot>{t('auditor:ncr.status.pendingVerification')}</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const getStatusIcon = (status: NCR['status']) => {
    switch (status) {
      case 'closed':
        return <CheckCircle2 className="w-5 h-5 text-success-600" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-warning-600" />;
      case 'open':
        return <AlertTriangle className="w-5 h-5 text-error-600" />;
      case 'pending_verification':
        return <FileText className="w-5 h-5 text-info-600" />;
    }
  };

  const getTypeBadge = (type: NCR['findingType']) => {
    switch (type) {
      case 'major':
        return <Badge variant="error">{t('auditor:ncr.types.major')}</Badge>;
      case 'minor':
        return <Badge variant="warning">{t('auditor:ncr.types.minor')}</Badge>;
      case 'observation':
        return <Badge variant="info">{t('auditor:ncr.types.observation')}</Badge>;
    }
  };

  const filteredNcrs = ncrs.filter((ncr) => {
    const matchesSearch = ncr.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ncr.ncrNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ncr.applicationId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ncr.status === statusFilter;
    const matchesType = typeFilter === 'all' || ncr.findingType === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  if (loading) {
    return (
      <PageLayout
        pageTitle={t('auditor:ncr.title')}
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: t('auditor:ncr.title') },
        ]}
      >
        <div className="animate-pulse space-y-4">
          <div className="h-20 bg-gray-200 rounded-lg"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle={t('auditor:ncr.title')}
      actions={
        <Button
          variant="primary"
          icon={<Plus className="w-4 h-4" />}
          onClick={() => navigate('/auditor/ncr/create')}
        >
          {t('auditor:ncr.createNCR')}
        </Button>
      }
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('auditor:ncr.title') },
      ]}
    >
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('auditor:ncr.stats.total')}</p>
                <p className="text-2xl font-bold text-gray-900">{ncrs.length}</p>
              </div>
              <FileText className="w-8 h-8 text-primary-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('auditor:ncr.stats.open')}</p>
                <p className="text-2xl font-bold text-error-600">
                  {ncrs.filter(n => n.status === 'open').length}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-error-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('auditor:ncr.stats.inProgress')}</p>
                <p className="text-2xl font-bold text-warning-600">
                  {ncrs.filter(n => n.status === 'in_progress').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-warning-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('auditor:ncr.stats.closed')}</p>
                <p className="text-2xl font-bold text-success-600">
                  {ncrs.filter(n => n.status === 'closed').length}
                </p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-success-600" />
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('auditor:ncr.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">{t('auditor:ncr.filters.allStatuses')}</option>
                <option value="open">{t('auditor:ncr.filters.open')}</option>
                <option value="in_progress">{t('auditor:ncr.filters.inProgress')}</option>
                <option value="pending_verification">{t('auditor:ncr.filters.pendingVerification')}</option>
                <option value="closed">{t('auditor:ncr.filters.closed')}</option>
              </select>
            </div>

            {/* Type Filter */}
            <div className="flex items-center gap-2">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">{t('auditor:ncr.filters.allTypes')}</option>
                <option value="major">{t('auditor:ncr.filters.major')}</option>
                <option value="minor">{t('auditor:ncr.filters.minor')}</option>
                <option value="observation">{t('auditor:ncr.filters.observation')}</option>
              </select>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* NCR List */}
      {filteredNcrs.length === 0 ? (
        <EmptyState
          icon={<FileText className="w-12 h-12" />}
          title={t('auditor:ncr.emptyState.title')}
          description={t('auditor:ncr.emptyState.description')}
          action={{
            label: t('auditor:ncr.emptyState.action'),
            onClick: () => navigate('/auditor/ncr/create'),
            icon: <Plus className="w-4 h-4" />,
          }}
        />
      ) : (
        <div className="space-y-4">
          {filteredNcrs.map((ncr) => (
            <Card
              key={ncr.id}
              hover
              className="cursor-pointer transition-shadow"
              onClick={() => navigate(`/auditor/ncr/${ncr.id}`)}
            >
              <CardBody>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    {/* Status Icon */}
                    <div className="mt-1">
                      {getStatusIcon(ncr.status)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {ncr.ncrNumber}
                        </h3>
                        {getStatusBadge(ncr.status)}
                        {getTypeBadge(ncr.findingType)}
                      </div>

                      <p className="text-base text-gray-900 mb-2">{ncr.companyName}</p>

                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{ncr.description}</p>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">{t('auditor:ncr.applicationId')}:</span>{' '}
                          {ncr.applicationId}
                        </div>
                        <div>
                          <span className="font-medium">{t('auditor:ncr.issuedDate')}:</span>{' '}
                          {formatDate(ncr.issuedDate)}
                        </div>
                        <div>
                          <span className="font-medium">{t('auditor:ncr.dueDate')}:</span>{' '}
                          <span className={
                            new Date(ncr.dueDate) < new Date() && ncr.status !== 'closed'
                              ? 'text-error-600 font-semibold'
                              : ''
                          }>
                            {formatDate(ncr.dueDate)}
                          </span>
                        </div>
                        <div className="md:col-span-3">
                          <span className="font-medium">{t('auditor:ncr.clauseReference')}:</span>{' '}
                          {ncr.clauseReference}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/auditor/ncr/${ncr.id}`);
                    }}
                  >
                    {t('auditor:ncr.viewDetails')}
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </PageLayout>
  );
};

